import modelCatalog from './aiModels.json';

/*
  AI Config: Human-Readable Control Panel
  --------------------------------------
  Goal:
  - Keep this file as editable settings + explainers only.
  - Runtime resolution logic lives in src/helpers/ai/aiConfigRuntimeHelpers.js.

  How to use:
  - Read sections in order (1..8), matching the execution flow logs.
  - Edit variables inside sections to change behavior.
  - Every scalar knob below has a single-line comment describing effect.
*/

/* Section 1: Model Source
   Models are managed in aiModels.json so this file can stay focused on behavior.
*/
export const AI_MODEL_CATALOG_SOURCE = './aiModels.json';
export const AI_MODEL_GROUPS = ['free', 'budget', 'premium'];

/* Section 2: Model Defaults
   Typical behavior:
   - One cheap model powers intent-contract, routing, reranking, consolidation, and summary helper calls.
   - The final response model stays user-selectable; these keys only define defaults when the user leaves selection on Default.
*/
export const AI_MODEL_DEFAULTS = {
  cheapModelKey: 'llama318b', // Default model key for all non-response helper/model passes.
  defaultResponseModelKey: 'gpt4oMini', // Global fallback response model key when no task-specific default applies.
  taskResponseModelKeys: {
    'chat.explore': 'gpt4oMini', // Default response model for Explore mode.
    'chat.write': 'gpt4oMini' // Default response model for Write mode.
  }
};

/* Section 3: Plan Stage (model call: no)
   Planner is deterministic in current code (query + budget planning).
*/
export const AI_FLOW_PLAN = {
  enabled: true, // Enables deterministic planning stage (query planning + budgets).
  notes: 'No model call in current implementation. Adjust budgets in retrieve section.' // Human note for operators.
};

/* Section 4: Retrieve Stage (model call: no)
   Retrieval is API/data-driven: active doc, chat history, linked docs, project search.
*/
export const AI_FLOW_RETRIEVE = {
  enabled: true, // Enables retrieval stage (active doc + linked docs + project search candidate generation).
  notes: 'No LLM call. This stage controls context quality and usually drives answer quality.', // Human note for operators.
  includeActiveDoc: true, // Allows active-doc extraction candidates to enter ranking.
  includeLinkedDocs: true, // Allows linked-doc extraction candidates to enter ranking.
  includeProjectSearch: true, // Allows project-search candidates to enter ranking.
  hotSignals: {
    enabled: true, // Enables hot-hit boosts from doc/folder/header lexical matches.
    docNameMultiplier: 5, // Multiplier for doc-name prompt matches.
    folderNameMultiplier: 2, // Multiplier for folder/path prompt matches.
    headerMultiplier: 5, // Multiplier for heading-level prompt matches.
    contentKeywordScore: 0.5, // Additive score for direct content keyword signal.
    contextDocMultiplier: 1.5, // Extra multiplier when doc is already in context set.
    headerTrailingChunks: 4, // Number of trailing chunks to piggyback after a hot heading hit.
    headerStopAtBoundary: true // Stops trailing expansion at heading boundary when true.
  },
  projectSearch: {
    excludeChatDocsWhenActiveChat: true, // When active doc.type is 'chat', exclude project-search rows where doc_type is 'chat'.
    deepLimitCap: 1200, // Upper bound for search limit during deepening retries; once reached, no further limit expansion is attempted.
    minNonChatRows: 6, // Minimum number of non-chat rows (doc_type != 'chat') targeted before stopping deepening retries.
    docDiversityEnabled: true, // Enables per-doc row diversity controls so one document does not dominate retrieval.
    perDocMaxChunks: 2, // Per document cap of selected rows/chunks in project-search candidate pass (applies to all doc types).
    perDocScoreDecay: 0.14, // Score decay applied to each additional selected row from the same document.
    adjacentChunkExpansion: {
      enabled: true, // Enables adjacent chunk piggyback expansion.
      maxChunksPerDoc: 3, // Maximum adjacent chunks per row/doc expansion.
      scoreDecayPerStep: 0.08, // Per-step score decay for each adjacent chunk.
      minSourceScore: 1.1, // Minimum source score required before allowing adjacency expansion.
      continueMinScore: 0.8 // Stops adjacency chain when decayed score drops below threshold.
    },
    chatNoise: {
      enabled: true, // Enables suppression/deprioritization for rows where doc_type is 'chat'.
      deprioritizeMultiplier: 0.45, // Score multiplier for chat-doc rows when non-chat alternatives exist.
      perDocMaxChunks: 1, // Per chat document (doc_type='chat') cap of rows/chunks retained.
      fallbackMultiplier: 0.2, // Score multiplier for chat-doc rows when only chat rows are available.
      nonChatPreferenceMultiplier: 1.9, // Positive multiplier for non-chat rows in chat-heavy contexts.
      maxChunks: 8, // Global cap of retained chat-doc rows/chunks per query after filtering/capping.
      searchLimitMultiplier: 8, // Multiplier used for deepening search limit while trying to discover non-chat rows.
      searchLimitCap: 240 // Upper cap for deepened limit used by chat-noise mitigation retries.
    },
    nameGuardrails: {
      exactScoreFloor: 18, // Minimum adjusted row score for exact doc-name match queries.
      exactMultiplier: 2.2, // Multiplier applied when project query exactly matches doc name.
      prefixMultiplier: 1.25 // Multiplier applied for strong prefix-level doc-name matches.
    },
    keywordSignal: {
      exactUniqueWeight: 0.09, // Weight per unique exact token hit in chunk text.
      exactTotalWeight: 0.03, // Weight per total exact token hits (capped).
      fuzzyUniqueWeight: 0.03, // Weight per unique fuzzy token hit in chunk text.
      fuzzyTotalWeight: 0.01, // Weight per total fuzzy token hits (capped).
      exactTotalCap: 10, // Cap on total exact hits counted per chunk score boost.
      fuzzyTotalCap: 8, // Cap on total fuzzy hits counted per chunk score boost.
      multiplierCap: 1.55, // Global cap for keyword-signal multiplier contribution.
      multiKeywordBoostPerExtra: 0.3, // Extra multiplicative boost per additional matched keyword in a chunk.
      multiKeywordBoostCap: 2.2, // Hard cap on multi-keyword chunk boost multiplier.
      diversityReserveEnabled: true, // Reserves candidate slots for different matched keywords to avoid one keyword dominating.
      diversityReservePerKeyword: 1, // Number of reserved candidates per tracked keyword.
      diversityMaxKeywords: 4, // Maximum keywords considered for diversity reservation.
      diversityDynamicReserveEnabled: true, // If true, reserve count per keyword expands when multiple candidates are close to top score.
      diversityDynamicTopScoreRatio: 0.5, // Keep keyword candidates scoring at least this fraction of that keyword's top score.
      diversityDynamicMaxPerKeyword: 4, // Upper bound for dynamic reserved candidates per keyword.
      diversityDynamicMinScore: 0.8 // Absolute floor for dynamic keyword reservation.
    },
    fullDocEscalation: {
      enabled: true, // Enables full-doc escalation during refine when chunk coverage is weak.
      // Applied on refine passes when keyword coverage stays weak.
      maxDocs: 1, // Maximum docs escalated to full-doc candidates.
      minCoverageRatio: 0.45, // Escalation trigger if project-search coverage ratio is below this.
      maxCharsPerDoc: 1800 // Character cap per escalated full-doc candidate.
    }
  },
  summaries: {
    enabled: true, // Enables summarize/backfill behavior for retrieval candidates.
    rerankEnabled: true, // Enables summary-driven reranking adjustments.
    maxPerTurn: 16, // Maximum total chunk hashes summarized per turn after all candidate sources are merged.
    refreshMinCandidateScore: 0.5, // Minimum candidate score to request summary refresh.
    refreshBackfillCapPerPrompt: 16, // Maximum chunk hashes submitted for summary backfill per prompt.
    minChunkChars: 180, // Minimum chunk length eligible for summary refresh/backfill.
    rerankBoostDelta: 0.18, // Additive boost when summary relevance says keep.
    rerankDownscoreDelta: 0.12, // Additive downscore when summary relevance says dump.
    rerankBoostMinMatchRatio: 0.45, // Minimum match ratio for boost eligibility.
    rerankDownscoreMaxMatchRatio: 0.1 // Maximum match ratio for downscore eligibility.
  },
  singleDocActiveDocBoost: 1.6, // Boost applied in single-doc contexts for active doc candidates.
  singleDocStrictActiveDoc: false, // If true, strongly constrains retrieval toward active doc in single-doc contexts.
  chatHistory: {
    enabled: true, // Enables chat-history candidates.
    lastQaPairs: 3, // Q/A pair count extracted from history for retrieval candidates.
    activeTailMaxChunks: 3, // Guaranteed active-chat tail turns appended after ranking.
    recencyWeight: 0.35, // Recency contribution weight in ranking.
    recencyDecayTurns: 8 // Recency half-life/decay window in turns.
  },
  limits: {
    maxLinkedDocs: 6, // Linked-doc load cap.
    maxProjectResults: 24, // Default project-search result limit before deepening logic.
    maxContextItems: 14, // Max selected context items sent forward.
    maxContextChars: 12000, // Max total context characters sent forward.
    attachmentSufficiencyMinItems: 2, // Min attachment items to skip project search.
    attachmentSufficiencyMinChars: 1200 // Min attachment chars to skip project search.
  }
};

/* Section 5: Select Stage (model call: no)
   Prompt/message assembly stage before request.
*/
export const AI_FLOW_SELECT = {
  enabled: true, // Enables select/message assembly stage.
  notes: 'No model call. This stage structures the request payload and context ordering.' // Human note for operators.
};

/* Section 6: Request + Stream Stage (model call: yes)
   This is the expensive, quality-sensitive stage.
*/
export const AI_FLOW_REQUEST_STREAM = {
  enabled: true, // Enables request + stream stage.
  notes: 'Final response defaults live here; cheap helper passes use AI_MODEL_DEFAULTS.cheapModelKey.', // Human note for operators.
  defaultChatTask: 'chat.explore', // Default task key used when chat mode is not explicitly mapped.
  chatModeToTask: {
    explore: 'chat.explore', // Maps Explore mode to task profile.
    write: 'chat.write' // Maps Write mode to task profile.
  },
  fallbackTierOrder: ['budget', 'free', 'mid-tier', 'premium'], // Retry order when primary model fails or is blocked.
  blockedModels: [
    'stepfun/step-3.5-flash:free', // Blocklist: model IDs not allowed for request stage.
    'z-ai/glm-4.5-air:free' // Blocklist: model IDs not allowed for request stage.
  ]
};

/* Section 7: Finalize Stage (model call: no)
   Deterministic response formatting/polish.
*/
export const AI_FLOW_FINALIZE = {
  enabled: true, // Enables deterministic finalization stage.
  notes: 'No model call. Controls output normalization/formatting behavior.', // Human note for operators.
  formatting: {
    enabled: true, // Enables response formatting pass.
    preserveMarkdown: true, // Preserves markdown structures in finalize normalization.
    collapseExtraWhitespace: true // Collapses repeated whitespace in finalize normalization.
  }
};

/* Section 8: Runtime Toggles
   Global orchestration toggles consumed by config.js and helpers.
*/
const CHAT_ORCHESTRATION = {
  enabled: true, // Master orchestration switch for staged AI flow.
  routingMode: 'hybrid', // Routing mode used by orchestration ('legacy' or 'hybrid').
  sufficiency: {
    enabled: true, // Enables sufficiency gate after retrieve.
    minItems: 3, // Minimum retrieved item count to consider context sufficient.
    minChars: 700, // Minimum retrieved character count to consider context sufficient.
    minCoverageRatio: 0.34, // Minimum keyword coverage ratio to consider context sufficient.
    minMatchedKeywords: 2, // Minimum matched keyword count to consider context sufficient.
    maxRefineLoops: 1 // Maximum refine loops when context is insufficient.
  },
  refine: {
    enabled: true, // Enables refine stage query expansion.
    maxExtraQueries: 2, // Maximum additional refine queries appended.
    budgetItemBoost: 4, // Extra context item budget added during refine.
    budgetCharBoost: 3000, // Extra character budget added during refine.
    budgetProjectResultsBoost: 8 // Extra project-result budget added during refine.
  },
  intentContract: {
    enabled: true, // Enables multi-output intent contract pass (intent + goal/retrieval hints).
    alwaysOn: false, // If true, runs model contract pass every turn (not only low-confidence turns).
    forceOnLongPrompts: true, // If true, prompts with many searchable keywords force model contract pass even when heuristic confidence is high.
    longPromptMinSearchableKeywords: 4, // Minimum searchable keyword count to trigger forced intent-contract model pass.
    maxPrimaryKeywords: 8, // Maximum primary keywords accepted from contract output.
    maxSecondaryKeywords: 12, // Maximum secondary keywords accepted from contract output.
    maxDocHints: 8, // Maximum doc hints accepted from contract output.
    docHintMultiplier: 1.35, // Score multiplier when contract doc hint matches a candidate doc name.
    folderHintMultiplier: 1.2, // Score multiplier when contract doc hint matches folder/path hints.
    includeProjectSummary: true, // Includes project summary context in intent contract prompt.
    includeLinkedDocSummaries: true, // Includes linked-doc summaries in intent contract prompt.
    maxLinkedDocSummaries: 6 // Maximum linked-doc summaries provided to intent contract model.
  },
  router: {
    enabled: false, // Enables router model pass before retrieval adjustments.
    timeoutMs: 2500, // Router call timeout.
    confidenceThreshold: 0.72, // Router confidence threshold for applying contract.
    maxCheapCallsPerTurn: 2, // Soft cap for cheap model subcalls per turn.
    maxTotalCallsPerTurn: 3 // Hard cap for all subcalls per turn.
  },
  reranker: {
    enabled: false, // Enables reranker model pass after retrieval.
    timeoutMs: 2500, // Reranker timeout.
    topDocShareThreshold: 0.65 // Gate for when reranker should engage by top-doc dominance.
  },
  consolidation: {
    enabled: true, // Enables consolidation model pass over large context sets.
    minContextItems: 10, // Minimum items required before consolidation triggers.
    minContextChars: 7000, // Minimum chars required before consolidation triggers.
    maxInputItems: 20, // Maximum items passed into consolidation model.
    maxInputChars: 18000, // Maximum chars passed into consolidation model.
    outputMaxChars: 2600, // Max chars allowed in consolidated output.
    keepTopItems: 2, // Number of top original items always retained.
    summaryHarvestMaxPerTurn: 6, // Maximum summarized chunk hashes harvested from consolidation input candidates.
    summaryHarvestMinScore: 0.75 // Minimum input chunk score required for consolidation summary harvest.
  },
  logging: {
    enabled: true, // Enables orchestration-level logging.
    attemptsEnabled: true, // Persists attempt-level logs.
    traceStageEventsEnabled: false, // Emits verbose stage-trace events when true.
    payloadsEnabled: false, // Stores request/response payload excerpts when true.
    payloadSampleRate: 0, // Sampling ratio (0..1) for payload logging.
    maxExcerptChars: 700, // Max chars for saved payload excerpt.
    attemptsIndexEnabled: true // Persists compact attempts index.
  },
  queryExpansion: {
    enabled: true, // Enables planner query expansion.
    maxQueries: 3, // Maximum planned queries submitted to retrieval.
    aliasGroups: {} // Optional alias templates for domain-specific expansion.
  },
  rollout: {
    allowLegacyFallback: true // Allows legacy one-shot request fallback on orchestration failures.
  },
  ui: {
    persistStageStatus: true, // Keeps stage status visible in UI.
    activityLogOnComplete: 'open' // UI behavior after completion: open|collapsed|hidden.
  },
  blockedModels: AI_FLOW_REQUEST_STREAM.blockedModels, // Global blocked-model list inherited from request-stream config.
  retrieval: {
    includeActiveDoc: AI_FLOW_RETRIEVE.includeActiveDoc, // Mirrors retrieve.includeActiveDoc.
    includeLinkedDocs: AI_FLOW_RETRIEVE.includeLinkedDocs, // Mirrors retrieve.includeLinkedDocs.
    includeProjectSearch: AI_FLOW_RETRIEVE.includeProjectSearch, // Mirrors retrieve.includeProjectSearch.
    projectSearch: AI_FLOW_RETRIEVE.projectSearch, // Mirrors retrieve.projectSearch knobs.
    singleDocActiveDocBoost: AI_FLOW_RETRIEVE.singleDocActiveDocBoost, // Mirrors retrieve.singleDocActiveDocBoost.
    singleDocStrictActiveDoc: AI_FLOW_RETRIEVE.singleDocStrictActiveDoc, // Mirrors retrieve.singleDocStrictActiveDoc.
    chatHistory: AI_FLOW_RETRIEVE.chatHistory, // Mirrors retrieve.chatHistory knobs.
    limits: AI_FLOW_RETRIEVE.limits // Mirrors retrieve.limits knobs.
  },
  formatting: AI_FLOW_FINALIZE.formatting // Mirrors finalize.formatting knobs.
};

const FLOW_NOTES = {
  modelDefaults: 'Cheap helper passes use one shared budget model; final response defaults are separate and user-overridable.',
  plan: 'Plan: no model call; deterministic query planning only.',
  retrieve: 'Retrieve: no model call; context APIs and ranking only.',
  select: 'Select: no model call; prompt/context assembly only.',
  requestStream: 'Request/Stream: main model call; premium can matter most here.',
  finalize: 'Finalize: no model call; deterministic output normalization.'
};

// Ordered execution map from prompt to response. Use this to understand what runs first, where logs appear,
// and which config section controls each step.
export const AI_FLOW_STAGE_SEQUENCE = [
  {
    order: 1, // HYDRATE stage order in logs.
    stage: 'hydrate', // Loads chat/doc/project state and context snapshots.
    logStage: 'HYDRATE', // Log stage label emitted by orchestration.
    modelCall: false, // No model call in this stage.
    configSections: ['CHAT_ORCHESTRATION.logging', 'CHAT_ORCHESTRATION.rollout'] // Primary knobs affecting this stage.
  },
  {
    order: 2, // GOAL_UPDATE stage order in logs.
    stage: 'goal_update', // Merges persistent goal/memory state.
    logStage: 'GOAL_UPDATE', // Log stage label emitted by orchestration.
    modelCall: false, // No model call in this stage.
    configSections: ['CHAT_ORCHESTRATION.sufficiency', 'CHAT_ORCHESTRATION.refine'] // Goal state informs later sufficiency/refine behavior.
  },
  {
    order: 3, // INTENT stage order in logs.
    stage: 'intent', // Heuristic intent + optional model fallback intent pass.
    logStage: 'INTENT', // Log stage label emitted by orchestration.
    modelCall: 'optional', // Model call only when heuristic confidence is low.
    configSections: ['AI_MODEL_DEFAULTS', 'CHAT_ORCHESTRATION.intentContract', 'AI_FLOW_REQUEST_STREAM.blockedModels'] // Intent contract knobs and shared cheap-model defaults.
  },
  {
    order: 4, // PLAN stage order in logs.
    stage: 'plan', // Deterministic query and budget planning.
    logStage: 'PLAN', // Log stage label emitted by orchestration.
    modelCall: false, // No model call in this stage.
    configSections: ['AI_FLOW_PLAN', 'CHAT_ORCHESTRATION.queryExpansion', 'CHAT_ORCHESTRATION.retrieval.limits'] // Planner knobs.
  },
  {
    order: 5, // RETRIEVE stage order in logs.
    stage: 'retrieve', // Context candidate collection/ranking from active, linked, search.
    logStage: 'RETRIEVE', // Log stage label emitted by orchestration.
    modelCall: false, // No model call in this stage.
    configSections: ['AI_FLOW_RETRIEVE', 'CHAT_ORCHESTRATION.retrieval', 'CHAT_ORCHESTRATION.router', 'CHAT_ORCHESTRATION.reranker'] // Retrieval and routing knobs.
  },
  {
    order: 6, // SUFFICIENCY stage order in logs.
    stage: 'sufficiency', // Decides if retrieval is enough to answer.
    logStage: 'SUFFICIENCY', // Log stage label emitted by orchestration.
    modelCall: false, // No model call in this stage.
    configSections: ['CHAT_ORCHESTRATION.sufficiency'] // Sufficiency thresholds.
  },
  {
    order: 7, // REFINE stage order in logs (when insufficient).
    stage: 'refine', // Adds refined queries/budgets then loops back to retrieve.
    logStage: 'REFINE', // Log stage label emitted by orchestration.
    modelCall: false, // No model call in this stage.
    configSections: ['CHAT_ORCHESTRATION.refine', 'CHAT_ORCHESTRATION.queryExpansion'] // Refine loop knobs.
  },
  {
    order: 8, // SUMMARIZE stage order in logs.
    stage: 'summarize', // Creates/refreshes chunk/doc summaries for selected chunks.
    logStage: 'SUMMARIZE', // Log stage label emitted by orchestration.
    modelCall: true, // Model call for summary extraction.
    configSections: ['AI_FLOW_RETRIEVE.summaries', 'CHAT_ORCHESTRATION.SUMMARY_*'] // Summary creation/refresh/rerank knobs.
  },
  {
    order: 9, // CONSOLIDATE stage order in logs.
    stage: 'consolidate', // Optional context compression pass before select/request.
    logStage: 'CONSOLIDATE', // Log stage label emitted by orchestration.
    modelCall: 'optional', // Only when consolidation enabled and gate passes.
    configSections: ['CHAT_ORCHESTRATION.consolidation'] // Consolidation gates and model.
  },
  {
    order: 10, // SELECT stage order in logs.
    stage: 'select', // Builds final messages/context payload sent to response model.
    logStage: 'SELECT', // Log stage label emitted by orchestration.
    modelCall: false, // No model call in this stage.
    configSections: ['AI_FLOW_SELECT', 'AI_FLOW_FINALIZE.formatting'] // Select/formatting interplay.
  },
  {
    order: 11, // REQUEST + STREAM stage order in logs.
    stage: 'request_stream', // Executes response model call and stream.
    logStage: 'REQUEST|STREAM', // Log stage labels emitted by orchestration.
    modelCall: true, // Main response model call.
    configSections: ['AI_FLOW_REQUEST_STREAM', 'CHAT_ORCHESTRATION.logging', 'CHAT_ORCHESTRATION.rollout'] // Model selection, retries, logging.
  },
  {
    order: 12, // FINALIZE stage order in logs.
    stage: 'finalize', // Deterministic output normalization and post-processing.
    logStage: 'FINALIZE', // Log stage label emitted by orchestration.
    modelCall: false, // No model call in this stage.
    configSections: ['AI_FLOW_FINALIZE', 'CHAT_ORCHESTRATION.formatting'] // Final formatting knobs.
  },
  {
    order: 13, // PERSIST stage order in logs.
    stage: 'persist', // Saves goal state, summaries, and request logs.
    logStage: 'PERSIST', // Log stage label emitted by orchestration.
    modelCall: false, // No model call in this stage.
    configSections: ['CHAT_ORCHESTRATION.logging', 'CHAT_ORCHESTRATION.ui'] // Persistence and visibility knobs.
  }
];

const FLOW_STAGE_MAPPING = {
  intent: {
    configSection: 'CHAT_ORCHESTRATION.intentContract',
    logStage: 'intent',
    editableOptions: ['CHAT_ORCHESTRATION.intentContract.*', 'AI_MODEL_DEFAULTS.cheapModelKey']
  },
  plan: {
    configSection: 'AI_FLOW_PLAN',
    logStage: 'plan',
    editableOptions: ['enabled', 'notes', 'CHAT_ORCHESTRATION.queryExpansion.*', 'CHAT_ORCHESTRATION.retrieval.limits.*']
  },
  retrieve: {
    configSection: 'AI_FLOW_RETRIEVE',
    logStage: 'retrieve',
    editableOptions: [
      'enabled',
      'includeActiveDoc',
      'includeLinkedDocs',
      'includeProjectSearch',
      'hotSignals.*',
      'projectSearch.excludeChatDocsWhenActiveChat',
      'projectSearch.deepLimitCap',
      'projectSearch.minNonChatRows',
      'projectSearch.docDiversityEnabled',
      'projectSearch.perDocMaxChunks',
      'projectSearch.perDocScoreDecay',
      'projectSearch.adjacentChunkExpansion.*',
      'projectSearch.chatNoise.*',
      'projectSearch.nameGuardrails.*',
      'projectSearch.keywordSignal.*',
      'projectSearch.fullDocEscalation.*',
      'summaries.*',
      'singleDocActiveDocBoost',
      'singleDocStrictActiveDoc',
      'chatHistory.*',
      'limits.*'
    ]
  },
  summarize: {
    configSection: 'AI_FLOW_RETRIEVE.summaries',
    logStage: 'summarize',
    editableOptions: ['enabled', 'rerankEnabled', 'refreshMinCandidateScore', 'refreshBackfillCapPerPrompt', 'minChunkChars', 'rerankBoostDelta', 'rerankDownscoreDelta', 'rerankBoostMinMatchRatio', 'rerankDownscoreMaxMatchRatio']
  },
  select: {
    configSection: 'AI_FLOW_SELECT',
    logStage: 'select',
    editableOptions: ['enabled', 'notes']
  },
  requestStream: {
    configSection: 'AI_FLOW_REQUEST_STREAM',
    logStage: 'request|stream',
    editableOptions: ['enabled', 'defaultChatTask', 'chatModeToTask.*', 'fallbackTierOrder', 'blockedModels', 'AI_MODEL_DEFAULTS.defaultResponseModelKey', 'AI_MODEL_DEFAULTS.taskResponseModelKeys.*']
  },
  consolidate: {
    configSection: 'CHAT_ORCHESTRATION.consolidation',
    logStage: 'consolidate',
    editableOptions: ['enabled', 'minContextItems', 'minContextChars', 'maxInputItems', 'maxInputChars', 'outputMaxChars', 'keepTopItems', 'model']
  },
  sufficiency: {
    configSection: 'CHAT_ORCHESTRATION.sufficiency',
    logStage: 'sufficiency',
    editableOptions: ['enabled', 'minItems', 'minChars', 'minCoverageRatio', 'minMatchedKeywords', 'maxRefineLoops']
  },
  refine: {
    configSection: 'CHAT_ORCHESTRATION.refine',
    logStage: 'refine',
    editableOptions: ['enabled', 'maxExtraQueries', 'budgetItemBoost', 'budgetCharBoost', 'budgetProjectResultsBoost']
  },
  finalize: {
    configSection: 'AI_FLOW_FINALIZE',
    logStage: 'finalize',
    editableOptions: ['enabled', 'formatting.*']
  },
  persist: {
    configSection: 'CHAT_ORCHESTRATION.logging',
    logStage: 'persist',
    editableOptions: ['enabled', 'attemptsEnabled', 'traceStageEventsEnabled', 'payloadsEnabled', 'payloadSampleRate', 'maxExcerptChars', 'attemptsIndexEnabled', 'CHAT_ORCHESTRATION.ui.*']
  }
};

// Practical runtime tuning guide: start with symptoms from logs, then adjust only listed knobs first.
export const AI_FLOW_TROUBLESHOOTING = [
  {
    symptom: 'Exact lexical matches do not rank clearly above weaker fuzzy matches', // Ranking symptom for exact vs fuzzy signal separation.
    inspectLogs: ['RETRIEVE selected', 'RETRIEVE reasons', 'SUMMARIZE docIds/chunkHashes'], // First logs to verify before tuning.
    tuneFirst: [
      'AI_FLOW_RETRIEVE.projectSearch.nameGuardrails.exactScoreFloor',
      'AI_FLOW_RETRIEVE.projectSearch.nameGuardrails.exactMultiplier',
      'AI_FLOW_RETRIEVE.projectSearch.keywordSignal.exactUniqueWeight',
      'AI_FLOW_RETRIEVE.projectSearch.keywordSignal.fuzzyUniqueWeight',
      'AI_FLOW_RETRIEVE.projectSearch.keywordSignal.multiplierCap'
    ],
    expectedDirection: 'Increase exact-name guardrails or exact keyword weights, and reduce fuzzy weights/caps to widen the good-vs-weak gap.' // Suggested tuning direction.
  },
  {
    symptom: 'Active chat gets drowned out by project-search chat noise', // Noise-control symptom while active chat is in focus.
    inspectLogs: ['RETRIEVE selected', 'RETRIEVE project-search rows', 'RETRIEVE reasons'], // Logs that show chat vs non-chat row balance.
    tuneFirst: [
      'AI_FLOW_RETRIEVE.projectSearch.chatNoise.deprioritizeMultiplier',
      'AI_FLOW_RETRIEVE.projectSearch.chatNoise.nonChatPreferenceMultiplier',
      'AI_FLOW_RETRIEVE.projectSearch.chatNoise.maxChunks',
      'AI_FLOW_RETRIEVE.chatHistory.activeTailMaxChunks'
    ],
    expectedDirection: 'Lower chat deprioritize multiplier and chat maxChunks, or increase active chat tail to preserve recent turns.' // Suggested tuning direction.
  },
  {
    symptom: 'Too many chunks from the same document dominate context', // Diversity symptom indicating per-doc cap/decay is too weak.
    inspectLogs: ['RETRIEVE selected', 'SELECT payload stats'], // Logs that reveal selected doc concentration.
    tuneFirst: [
      'AI_FLOW_RETRIEVE.projectSearch.docDiversityEnabled',
      'AI_FLOW_RETRIEVE.projectSearch.perDocMaxChunks',
      'AI_FLOW_RETRIEVE.projectSearch.perDocScoreDecay',
      'AI_FLOW_RETRIEVE.limits.maxContextItems'
    ],
    expectedDirection: 'Reduce per-doc max chunks and increase per-doc decay to spread context across more docs.' // Suggested tuning direction.
  },
  {
    symptom: 'Context is often insufficient and refine loops trigger too aggressively', // Sufficiency/refine loop symptom.
    inspectLogs: ['SUFFICIENCY decision', 'REFINE iteration', 'RETRIEVE selected'], // Logs to diagnose false-insufficient decisions.
    tuneFirst: [
      'CHAT_ORCHESTRATION.sufficiency.minItems',
      'CHAT_ORCHESTRATION.sufficiency.minChars',
      'CHAT_ORCHESTRATION.sufficiency.minCoverageRatio',
      'CHAT_ORCHESTRATION.refine.maxExtraQueries'
    ],
    expectedDirection: 'Lower sufficiency thresholds first if good evidence exists but loops still trigger; then reduce refine query count if cost is high.' // Suggested tuning direction.
  },
  {
    symptom: 'Search logs are too noisy for diagnosis', // Observability symptom when debugging is hard.
    inspectLogs: ['RETRIEVE project-search rows', 'RETRIEVE selected'], // Logs most affected by row volume.
    tuneFirst: [
      'CHAT_ORCHESTRATION.logging.enabled',
      'CHAT_ORCHESTRATION.logging.traceStageEventsEnabled',
      'CHAT_ORCHESTRATION.logging.payloadsEnabled',
      'CHAT_ORCHESTRATION.logging.maxExcerptChars'
    ],
    expectedDirection: 'Disable trace/payload logging or shrink excerpts to keep retrieval reason lines readable.' // Suggested tuning direction.
  }
];

export const AI_CONFIG = {
  metadata: {
    title: 'AI Flow Config',
    intent: 'Editable settings aligned to runtime flow stages and logs.',
    modelCatalogSource: AI_MODEL_CATALOG_SOURCE,
    modelGroups: AI_MODEL_GROUPS,
    stageMapping: FLOW_STAGE_MAPPING,
    stageSequence: AI_FLOW_STAGE_SEQUENCE, // Ordered step-by-step execution map from prompt to response.
    troubleshooting: AI_FLOW_TROUBLESHOOTING // Symptom-to-knob playbook for runtime tuning.
  },
  flow: {
    plan: AI_FLOW_PLAN,
    retrieve: AI_FLOW_RETRIEVE,
    select: AI_FLOW_SELECT,
    requestStream: AI_FLOW_REQUEST_STREAM,
    finalize: AI_FLOW_FINALIZE,
    notes: FLOW_NOTES
  },
  routing: {
    defaultChatTask: AI_FLOW_REQUEST_STREAM.defaultChatTask,
    chatModeToTask: AI_FLOW_REQUEST_STREAM.chatModeToTask,
    defaultCheapModelKey: AI_MODEL_DEFAULTS.cheapModelKey,
    defaultResponseModelKey: AI_MODEL_DEFAULTS.defaultResponseModelKey,
    taskResponseModelKeys: AI_MODEL_DEFAULTS.taskResponseModelKeys,
    fallbackTierOrder: AI_FLOW_REQUEST_STREAM.fallbackTierOrder
  },
  chatOrchestration: CHAT_ORCHESTRATION,
  modelCatalog
};

export default AI_CONFIG;
